import { useCallback, useEffect, useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Badge,
  useToast,
  Text,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchEmployerDataAsync,
  selectEmployerData,
  selectPagination,
  verifyEmployerAsync,
} from "../../EmployerData/EmployerDataSlice";
import { TimeDifference } from "../../../common/TimeDifference";
import { TimeDifferenceadmin } from "../../../common/TimeDifferenceadmin";
import { exportEmployersToExcel } from "../../EmployerData/exportEmployers";

const EmployerList = () => {
  const dispatch = useDispatch();
  const empolyerdata = useSelector(selectEmployerData);
  const pagination = useSelector(selectPagination);
  const toast = useToast();

  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Fetches employers from the admin `fetch-employer-list` endpoint.
  const getEmployers = useCallback(
    (page = currentPage) => {
      setIsLoading(true);
      return dispatch(fetchEmployerDataAsync(page))
        .unwrap()
        .then(() => setIsLoading(false))
        .catch((error) => {
          setIsLoading(false);
          toast({
            title: "Could not fetch employers",
            description:
              typeof error === "string"
                ? error
                : "There was an error fetching the employer list.",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
        });
    },
    [dispatch, currentPage, toast]
  );

  useEffect(() => {
    getEmployers(currentPage);
  }, [getEmployers, currentPage]);

  const handleExportExcel = () => {
    if (!empolyerdata || empolyerdata.length === 0) {
      toast({
        title: "Nothing to export",
        description: "Click \"Get Employers\" to load employers first.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }
    exportEmployersToExcel(empolyerdata);
    toast({
      title: "Export started",
      description: `Exporting ${empolyerdata.length} employer(s) to Excel.`,
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "top-right",
    });
  };

  const handlePrevPage = () => {
    if (pagination.current_page > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.current_page < pagination.last_page) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handleVerifyClick = (employer) => {
    setSelectedEmployer(employer);
    onOpen();
  };

  const handleVerifyConfirm = () => {
    if (selectedEmployer) {
      dispatch(
        verifyEmployerAsync({
          uuid: selectedEmployer.uuid,
          verifyData: { status: true },
        })
      )
        .unwrap()
        .then(() => {
          onClose();
          toast({
            title: "Employer Verified",
            description: "The employer has been successfully verified.",
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
          dispatch(fetchEmployerDataAsync(currentPage));
        })
        .catch((error) => {
          console.error("Verification failed:", error);
          toast({
            title: "Verification Failed",
            description: "There was an error verifying the employer.",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
          onClose();
        });
    }
  };

  const handleNotVerify = () => {
    if (selectedEmployer) {
      dispatch(
        verifyEmployerAsync({
          uuid: selectedEmployer.uuid,
          verifyData: { status: false },
        })
      )
        .unwrap()
        .then(() => {
          onClose();
          toast({
            title: "Employer Not Verified",
            description: "The employer has not been verified.",
            status: "info",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
          dispatch(fetchEmployerDataAsync(currentPage));
        })
        .catch((error) => {
          console.error("Verification failed:", error);
          toast({
            title: "Verification Failed",
            description: "There was an error verifying the employer.",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
          onClose();
        });
    }
  };

  // Skeleton loading component for table rows
  const SkeletonRow = () => (
    <Tr>
      <Td><Skeleton height="20px" width="20px" /></Td>
      <Td><Skeleton height="20px" width="120px" /></Td>
      <Td><Skeleton height="20px" width="150px" /></Td>
      <Td><Skeleton height="20px" width="100px" /></Td>
      <Td><Skeleton height="20px" width="80px" /></Td>
      <Td><Skeleton height="20px" width="90px" /></Td>
      <Td><Skeleton height="20px" width="100px" /></Td>
      <Td><Skeleton height="20px" width="60px" /></Td>
    </Tr>
  );

  return (
    <div className="contentAdmin bg-adminbg rounded p-4">
      <div
        className="bg-white my-5 rounded-md"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <div className="flex justify-between items-center">
          <p className="text-xl text-black font-semibold px-3 py-7">
            Employer List
          </p>
          <div className="flex items-center gap-2 px-3">
            <Button
              colorScheme="blue"
              size="sm"
              isLoading={isLoading}
              loadingText="Fetching"
              onClick={() => getEmployers(currentPage)}
            >
              Get Employers
            </Button>
            <Button
              colorScheme="green"
              size="sm"
              variant="outline"
              isDisabled={!empolyerdata || empolyerdata.length === 0}
              onClick={handleExportExcel}
            >
              Export to Excel
            </Button>
          </div>
        </div>
        <TableContainer>
          <Table size="sm">
            <Thead className="bg-gray-200">
              <Tr>
                <Th>ID</Th>
                <Th>Organization</Th>
                <Th>Contact Name</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>Job Title</Th>
                <Th>Job Type</Th>
                <Th>Openings</Th>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading ? (
                // Show skeleton rows while loading
                Array.from({ length: 5 }).map((_, index) => (
                  <SkeletonRow key={index} />
                ))
              ) : empolyerdata && empolyerdata.length > 0 ? (
                empolyerdata.map((data, index) => (
                  <Tr key={data.uuid || data.Email || data.email || index}>
                    <Td>{index + 1}</Td>
                    <Td>{data.Orgination || data.company_name || data.name || "-"}</Td>
                    <Td>{data.Contact_Name || data.contact_name || "-"}</Td>
                    <Td textColor={"green"}>{data.Email || data.email || "-"}</Td>
                    <Td>
                      <div className="text-sm opacity-90 text-blue-700">
                        {data.Phone || data.phone || "-"}
                      </div>
                    </Td>
                    <Td>{data.Job_Title || data.job_title || "-"}</Td>
                    <Td>{data.Job_Type || data.job_type || "-"}</Td>
                    <Td>
                      {data.Opening !== undefined && data.Opening !== null
                        ? data.Opening
                        : "-"}
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan="8" className="text-center py-4">
                    No data available
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </div>

      {/* Pagination with skeleton loading */}
      <div className="flex justify-center my-2">
        {isLoading ? (
          <div className="flex items-center gap-4">
            <Skeleton height="40px" width="80px" />
            <Skeleton height="20px" width="120px" />
            <Skeleton height="40px" width="80px" />
          </div>
        ) : (
          <>
            <Button
              onClick={handlePrevPage}
              isDisabled={pagination.current_page === 1}
              colorScheme="blue"
              mx={2}
              className="bg-blue-500 text-white px-4 py-2 rounded"
            >
              Previous
            </Button>
            <span className="px-4 py-2 text-lg font-medium">
              Page {pagination.current_page} of {pagination.last_page}
            </span>
            <Button
              onClick={handleNextPage}
              isDisabled={pagination.current_page === pagination.last_page}
              colorScheme="blue"
              mx={2}
              className="bg-blue-500 text-white px-4 py-2 rounded"
            >
              Next
            </Button>
          </>
        )}
      </div>

      {/* Verification Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent maxW="2xl" borderradius="lg" boxShadow="lg">
          <ModalHeader fontSize="lg" fontWeight="bold" textAlign="center">
            Verify Employer
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <Text fontSize="md" textAlign="center">
              Do you want to verify this employer{" "}
              <Text as="span" fontWeight="semibold" color="red.500">
                "{selectedEmployer?.name}"
              </Text>
              ?
            </Text>
          </ModalBody>
          <ModalFooter display="flex" gap="2" justifyContent="center">
            <Button colorScheme="blue" w="40" onClick={handleVerifyConfirm}>
              Yes
            </Button>
            <Button
              variant="outline"
              colorScheme="blue"
              w="40"
              onClick={handleNotVerify}
            >
              No
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default EmployerList;